import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import TextTruncate from 'react-text-truncate';

import CardModule from '../../shared/card-module';
import ModalBasic from '../../../atoms/modal-basic';
import format from '../../../../utils/format';
import { get as gs } from '../../../../utils/GlobalCssProxy';
import useProfileAppContext from '../../../../stores/profile/use-profile-app-context';

const renderFactSheetButton = href => {
  if (!href) {
    return null;
  }
  return (
    <Button
      variant={'outline-black'}
      className={gs('btn--rounded', 'font-weight-bold', 'text-uppercase', 'd-print-none')}
      size="sm"
      role="button"
      as="a"
      href={href}
      target="_blank"
    >
      View Fact Sheet
    </Button>
  );
};

/**
 * Investment Strategy Module
 */
const InvestmentStrategy = ({ children, heading, factSheetUrl, fundName, extend }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleShow = e => setIsModalVisible(true);

  const handleClose = e => setIsModalVisible(false);

  return (
    <CardModule
      data-analytics-componenttype="third-width-component"
      data-analytics-id="TW_InvestmentStrategy"
      data-analytics-section="component"
    >
      <CardModule.Header>{heading}</CardModule.Header>
      <CardModule.Body>
        {/* TODO: consider making this a sub-component, or otherwise refactor to facilitate testing */}
        {extend ? <ExtendedText
          extendedText = {children}
          clickHandle= {handleShow}
        /> :
          <TextTruncate
            containerClassName={gs('paragraph-standard', 'd-print-none')}
            line={8}
            text={children}
            truncateText="..."
            textTruncateChild={
              <Button
                variant="link--reverse-underline"
                onClick={handleShow}
                className={gs('text-black', 'p-0', 'd-inline')}
              >
              View full description
              </Button>
            }
          /> }
        <p className={gs('d-none', 'd-print-block')}>
          {children}
        </p>
      </CardModule.Body>
      <CardModule.Footer>
        {renderFactSheetButton(factSheetUrl)}
      </CardModule.Footer>
      <ModalBasic show={isModalVisible} onHide={handleClose}>
        <ModalBasic.Header>
          <ModalBasic.Title>
            {heading}
            <ModalBasic.Subtitle>{fundName}</ModalBasic.Subtitle>
          </ModalBasic.Title>
          <ModalBasic.CloseButton onClick={handleClose} />
        </ModalBasic.Header>
        <ModalBasic.Body>{extend ? children.modalText : children}</ModalBasic.Body>
        <ModalBasic.Footer className={gs('justify-content-start')}>
          {renderFactSheetButton(factSheetUrl)}
        </ModalBasic.Footer>
      </ModalBasic>
    </CardModule>
  );
};

InvestmentStrategy.propTypes = {
  /**
   * The content that will be displayed truncated in the module and fully in the modal.
   * Truncation requires this content to be text, or markup that can be treated as text.
   */
  children: PropTypes.node,
  /**
   * Heading displayed on module and modal.
   */
  heading: PropTypes.string,
  /**
   * URL to use for fact sheet buttons/links
   */
  factSheetUrl: PropTypes.string,
  /**
   * Fund name displayed in modal sub-heading
   */
  fundName: PropTypes.string,
  /**
   * text to be truncated or not
   */
  extend: PropTypes.bool
};

InvestmentStrategy.defaultProps = {
  heading: 'Investment Strategy',
  fundName: format.NO_VALUE
};

const ExtendedText = ({ extendedText, clickHandle }) => {
  return (
    <Row className= {gs(
      'ml-2',
      'mr-2',
      'mt-4',
      'mb-5',
      'd-print-none')}>
      <Row>
        <p className={gs('paragraph-emphasized', 'mr-6')}>
          {extendedText.headline}
        </p>
        <p className={gs('paragraph-standard', 'mr-6')}>{extendedText.body}</p>
      </Row>
      <Row>
        <Button
          variant="link--reverse-underline"
          onClick={clickHandle}
          className={gs(
            'text-black',
            'p-0',
            'd-inline')}
        >
              View more information
        </Button>
      </Row>
    </Row>
  );
};

ExtendedText.propTypes = {
  /**
   * Text to be displayed on card
   */
  extendedText: PropTypes.shape({
    headline: PropTypes.string,
    body: PropTypes.string
  }),
  /**
   * Function for View more information button
   */
  clickHandle: PropTypes.func
};

export default InvestmentStrategy;
